import { m, AnimatePresence } from 'motion/react';
import { cn } from './utils/styles';
import { SuperbarMenu } from './SuperbarMenu';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';
import { SubmitButton } from './SubmitButton';
import { VoiceInputButton } from './VoiceInputButton';
import { VoiceModeButton } from './VoiceModeButton';
import type { VoiceModeState } from './hooks/useVoiceMode';

const FADE_SCALE_TRANSITION = {
  initial: { opacity: 0, scale: 0.92 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.92 },
  transition: { duration: 0.15, ease: 'easeOut' as const },
};

type InputVoiceProps = {
  isListening: boolean;
  voiceDisabled: boolean;
  voiceDisabledTooltip?: string;
  startListening: () => void;
  stopListening: () => void;
  voiceModeState?: VoiceModeState;
  toggleVoiceMode?: () => void;
  isVoiceModeAvailable?: boolean;
  voiceModeDisabled?: boolean;
  voiceModeDisabledTooltip?: string;
};

type InputActionButtonsProps = {
  isExpanded: boolean;
  value: string;
  isTyping: boolean;
  loading: boolean;
  isVoiceAvailable: boolean;
  voice: InputVoiceProps;
  handleClearAllAIFilters: () => void;
};

export const InputActionButtons = ({
  isExpanded,
  value,
  isTyping,
  loading,
  isVoiceAvailable,
  voice,
  handleClearAllAIFilters,
}: InputActionButtonsProps) => {
  const {
    isListening,
    voiceDisabled,
    voiceDisabledTooltip,
    startListening,
    stopListening,
    voiceModeState,
    toggleVoiceMode,
    isVoiceModeAvailable,
    voiceModeDisabled,
    voiceModeDisabledTooltip,
  } = voice;
  const theme = useSuperbarTheme();

  const isIdle = !loading && !isListening;
  const hideForTyping = isTyping && !!value;
  const showDictationButton =
    isVoiceAvailable && !isListening && !loading && !hideForTyping;
  const showVoiceModeButton = isVoiceModeAvailable && isIdle && !hideForTyping;

  return (
    <div
      className={cn(
        'caddietw-flex-shrink-0 caddietw-z-50 caddietw-border-none caddietw-flex caddietw-justify-end caddietw-gap-1 caddietw-px-3',
        isExpanded
          ? 'caddietw-pt-[10px] caddietw-items-start'
          : 'caddietw-self-stretch caddietw-items-center'
      )}
    >
      <AnimatePresence>
        {showDictationButton && (
          <m.div
            key="dictation"
            {...FADE_SCALE_TRANSITION}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VoiceInputButton
              onClick={startListening}
              disabled={voiceDisabled}
              tooltipLabel={voiceDisabledTooltip}
            />
          </m.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showVoiceModeButton && (
          <m.div
            key="voice-mode"
            {...FADE_SCALE_TRANSITION}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VoiceModeButton
              voiceModeState="off"
              onClick={toggleVoiceMode ?? (() => {})}
              disabled={voiceModeDisabled}
              disabledTooltip={voiceModeDisabledTooltip}
            />
          </m.div>
        )}
      </AnimatePresence>
      {!isListening && (isExpanded || !value) && (
        <SuperbarMenu onClear={handleClearAllAIFilters} />
      )}
      {!isExpanded && !isListening && value && (
        <SubmitButton
          isLoading={loading}
          iconColor={theme.components.assistantMessage.ctaButton.background}
          size={28}
          disabled={!value}
        />
      )}
    </div>
  );
};
